import { ReactElement } from 'react'

import { CartGridRowProps } from '../../client/components/cart/src/cart-grid.js'
import { ErrorInSubscriptionDataSimple } from '../errors/subscription-errors.js'
import { translate } from '../i18n.js'
import { calculateGross, calculateNet } from '../utils/maths.js'
import { formatMonthlyPrice } from '../utils/utils.js'
import { CommonSubscriptionProduct } from './common-subscription-product.js'

export const MIN_FLEX_DISCOUNT_DURATION = 12

export class CommonSubscriptionFlexProduct extends CommonSubscriptionProduct {
    get debug() {
        super.debug
        console.log({
            class: 'SubscriptionFlexProduct',
            durations: this.discountDurations,
            ceiledDuration: this.ceiledDiscountDuration,
            firstPaymentAfterDiscountPeriod: this.firstPaymentAfterDiscountPeriod,
            priceDescription: this.priceDescription,
            regularDiscountNet: this.regularDiscountNet,
            monthlyRegularDiscountNet: this.monthlyRegularDiscountNet,
            totalNet: this.totalNet,
            totalGross: this.totalGross,
            monthlyIgnoreVatDiscounts: this.monthlyIgnoreVatDiscounts,
            discountedPrice: this.discountedPrice,
            displayedPriceNumber: this.displayedPriceNumber,
            displayedPrice: this.displayedPrice,
            discounts: this.discounts,
            baseSubscriptionCostNet: this.baseSubscriptionCostNet,
            ceiledDiscountDuration: this.ceiledDiscountDuration,
        })

        return 1
    }

    get planDuration() {
        return this.ceiledDiscountDuration
    }

    get discountDurations() {
        return {
            general: this.regularDiscountNet / this.baseSubscriptionCostNet,
            ignoreVat: this.discounts.ignoreVat / this.baseSubscriptionCostGross,
        }
    }

    get minPossibleDiscountDuration() {
        return Math.max(this.discountDuration, MIN_FLEX_DISCOUNT_DURATION)
    }

    get ceiledDiscountDuration() {
        return Math.ceil(this.minPossibleDiscountDuration)
    }

    get discountDuration() {
        return this.discountDurations.general + this.discountDurations.ignoreVat
    }

    get firstPaymentAfterDiscountPeriod() {
        if (
            this.minPossibleDiscountDuration >= MIN_FLEX_DISCOUNT_DURATION &&
            this.minPossibleDiscountDuration % 1 > 0
        ) {
            const summedDuration = (this.discountDurations.general + this.discountDurations.ignoreVat) % 1
            const newGrossPrice = this.baseSubscriptionCostGross * (1 - summedDuration)

            return newGrossPrice
        }

        return null
    }

    get priceDescription(): string | ReactElement {
        if (this.discountDuration > 0) {
            return translate('plans.plans.FLEX.priceDescription', {
                lng: this.locale,
                basePrice: formatMonthlyPrice(this.baseSubscriptionCostGross, this.currencySymbol, this.locale),
                firstDiscountedPrice: this.firstPaymentAfterDiscountPeriod
                    ? formatMonthlyPrice(this.firstPaymentAfterDiscountPeriod, this.currencySymbol, this.locale)
                    : '',
                months: this.ceiledDiscountDuration,
            })
        }

        return ''
    }

    get cartRows() {
        const rows: CartGridRowProps[] = []
        const hasDiscount = this.ceiledDiscountDuration > 0

        rows.push({
            left: {
                text: translate('cart.monthlyCost', {
                    lng: this.locale,
                }),
            },
            right: {
                isBlue: hasDiscount,
                text: this.displayedPrice,
            },
        })

        if (hasDiscount) {
            rows.push({
                left: {
                    text: this.priceDescription,
                    weight: 'regular',
                },
            })
        }

        return {
            monthly: this.displayedPriceNumber,
            rows,
        }
    }

    get regularDiscountNet() {
        if (!this.plan.countryProductVat) {
            throw new ErrorInSubscriptionDataSimple('No VAT for plan')
        }

        return calculateNet(this.discounts.general, this.plan.countryProductVat)
    }

    get monthlyRegularDiscountNet() {
        return this.regularDiscountNet / this.ceiledDiscountDuration
    }

    get totalNet() {
        return this.plan.monthlyPriceNumberNet - this.monthlyRegularDiscountNet
    }

    get totalGross() {
        if (!this.plan.countryProductVat) {
            throw new ErrorInSubscriptionDataSimple('No VAT for plan')
        }

        return calculateGross(this.totalNet, this.plan.countryProductVat)
    }

    get monthlyIgnoreVatDiscounts() {
        return this.discounts.ignoreVat / this.ceiledDiscountDuration
    }

    get discountedPrice() {
        return Math.max(this.totalGross - this.monthlyIgnoreVatDiscounts, 0)
    }

    get displayedPriceNumber() {
        return this.ceiledDiscountDuration > 0 ? this.discountedPrice : this.baseSubscriptionCostGross
    }

    get displayedPrice() {
        return formatMonthlyPrice(this.displayedPriceNumber, this.currencySymbol, this.locale)
    }

    get minimalTotalCost() {
        return this.displayedPriceNumber
    }
}
